import { Typography } from "@mui/material"
import { SolexDisplay } from './SolexDisplay';
import ElliHost from "../../services/elliHost"
import { useDispatch, useSelector } from "react-redux"
import { IBorrowerTransactionRequest, ITransactionRequest, IBorrowerPairTransactionId } from "../../models/ITransactionRequest"
import { RootState, store } from "../../state/store";
import { addRequestId, setLoadingState, setOrderCount, setOrderSolexEditorLink } from "../../state/appSlice"
import { API_REQUEST_STATUS, BUTTON_LABELS, DELIVERY_PREFIX, EMAIL_REGEX, EPC_REQUEST_TYPES, REQUEST_TYPES, SCOPE_TYPE } from "../../utils/constants";
import { IDeliveryType, IExpression } from "../../models/configuration/plugin/IConfiguration";
import { SpecialDeliveryTypes } from "../../enums/SpecialDeliveryTypes";
import { useState, useEffect } from "react";
import EmailInput from "./EmailInput";
import { IDocListItem } from "../../models/IDocListResponse";
import ErrorModal from "./ErrorModal";
import { IApiError, IConformXErrors, IDocGenStatusModel, IDocGenConformXResponse, IErrorDetail } from "../../models/IDocGenStatusModel";
import DocGenApiService from "../../services/docGenApiService";
import GenericDialog from "../shared/GenericDialog";
import PreviewDocsService from "../../services/previewDocsService";
import { useTypedSelector } from "../../hooks/useTypedSelector";
import { IRequestId } from "../../models/IRequestIds";
import { GetExpressionEvaluationResults, addRegexExp } from "../../utils/expressionEvaluationHelper";
import expressionService from '../../services/expressionService';
import { IExpressionEvaluation } from "../../models/IExpressionValidation";
import { useEnhancedFieldIdValuePairs } from "../../hooks/useEnhancedFieldIdValuePairs";
import PluginService from "../../services/pluginService";
import { ISaveOrderDetailRequest } from "../../models/ISaveOrderDetailRequest";
import { StyledHeaderButton } from "../../layout/Header.styles";
import { DtStatusDialog } from '../ui/dialog';
import { useSolexEditor } from '../../hooks/useSolexEditor';
import { FeatureFlags, useFeatureFlag } from "../../featureFlags";

const BORROWER_TX_MAX_RETRIES = 2;
const BORROWER_TX_RETRY_DELAY_MS = 1000;

const OrderButton =
    (props: {
        requestType: string,
        packageId: string,
        parentTypeId: string,
        altPackageId: string,
        deliveryMethods: Array<IDeliveryType>
        disabled: boolean | undefined
        alertHandler: any
        selectedDocuments: Array<IDocListItem>
        token: string,
        alwaysPreviewDocuments: boolean
        buttonText: string
        sendValidations: IExpression[] | null
        startIcon?: React.ReactNode
    }) => {
        let epcTransactionType = EPC_REQUEST_TYPES.DISCLOSURES;
        const elliHost = ElliHost.getInstance();
        const dispatch = useDispatch();
        const expService = new expressionService();
        const pluginService = new PluginService();
        const [emails, setEmails] = useState<string>('')
        const [deliveryEmails, setDeliveryEmails] = useState<Array<string>>([]);
        const [collabEmails, setCollabEmails] = useState<Array<string>>([]);
        const [openEmailModal, setOpenEmailModal] = useState<boolean>(false);
        const [emailModalText, setEmailModalText] = useState<string>('');
        const [emailInputType, setEmailInputType] = useState<'email' | 'collab'>('email');
        const [emailInputError, setEmailInputError] = useState<boolean>(false);
        const [submitSolexErrors, setSubmitSolexErrors] = useState<Array<IErrorDetail> | null>(null);
        const [submitErrors, setSubmitErrors] = useState<IConformXErrors | null>(null);
        const [submitAPiError, setSubmitApiError] = useState<IApiError | null>(null);
        const [errorOpen, setErrorOpen] = useState<boolean>(false);
        const [successOpen, setSuccessOpen] = useState<boolean>(false);
        const [orderFailOpen, setOrderFailOpen] = useState<boolean>(false);
        const [conformXURL, setConformXURL] = useState<string>('');
        const appState = useSelector((state: RootState) => state.appSlice);
        const currentOrderCount = (appState as any).orderState?.orderCount || 0;
        const requests = useTypedSelector<Array<IRequestId>>((state) => appState.requestIds);
        const accessToken = useSelector((state) => appState.accessToken);
        const solexEditorLinkFromRedux = useSelector((state: RootState) => state.appSlice.orderState.solexEditorLink);
        const enhancedFieldIdValuePairs = useEnhancedFieldIdValuePairs();
        const [expValidationDialogOpen, setExpValidationDialogOpen] = useState<boolean>(false);
        const [expValidationDialogBody, setExpValidationDialogBody] = useState<string>('');
        const [triggerSubmission, setTriggerSubmission] = useState<boolean>(false);
        const [solexWarnings, setSolexWarnings] = useState<string[] | null>(null);
        const isSolexEditorFlagEnabled = useFeatureFlag(FeatureFlags.UI_SOLEX_EDITOR_ENABLED);

        // Solex Document Editor integration with feature flag (EN-3255)
        const solexEditor = useSolexEditor(props.alertHandler);

        const isClosingPackage = () => {
            const normalizedPackageId = props.packageId?.toLowerCase() ?? "";
            return normalizedPackageId.includes("closing") || normalizedPackageId.includes("shipping");
        }

        const validateLoanAndConfig = (): boolean => {
            const currentState = store.getState();
            if (!currentState.appSlice.loanId) {
                props.alertHandler('No Loan ID', 'error');
                return false;
            }
            if (!currentState.appSlice.configId) {
                props.alertHandler('No Config ID', 'error');
                return false;
            }
            return true;
        };

        const isPrintInPersonSelected = () =>
            props.deliveryMethods?.some(
                (deliveryMethod) =>
                    deliveryMethod.Id?.toLowerCase() === SpecialDeliveryTypes.PrintInPerson.toLowerCase()
            ) ?? false;

        const buildTransactionObject = (borrowerTransactionIds: IBorrowerPairTransactionId[] = []) => {
            const currentState = store.getState();            
            if (!validateLoanAndConfig()) {
                return;
            }
            let enablePreview = false;
            let requestType = REQUEST_TYPES.DOCUMENT_REQUEST_IMPORT;
            if (props.requestType === REQUEST_TYPES.LIGHTS_OUT) {
                requestType = REQUEST_TYPES.DOCUMENT_REQUEST;
                enablePreview = props.deliveryMethods.length < 1;
            }

            //frmDocutech.cs for these values
            let ronIndicator = false;
            let eNotaryIndicator = false;
            let eNoteIndicator = false;
            let eDiscloseIndicator = props.deliveryMethods?.some(d => d.Id?.toLowerCase().startsWith(DELIVERY_PREFIX.EDISCLOSE)) ?? false;
            let eSignIndicator = props.deliveryMethods?.some(d => d.Id?.toLowerCase().startsWith(DELIVERY_PREFIX.ESIGN)) ?? false;
            let eCloseIndicator = props.deliveryMethods?.some(d => d.Id?.toLowerCase().startsWith(DELIVERY_PREFIX.ECLOSE)) ?? false;
            let eNotaryType = "";
            let verificationVendorType = "";
            let formFreeLenderId = "";
            let iPenVendor = "";
            let iFrameVendor = props.deliveryMethods?.find(d => d.CustomAttributes.IframeVendor)?.CustomAttributes?.IframeVendor ?? "";

            // Only enable Solex for eligible delivery types (eSign/eClose, not eDisclose) (EN-3255)
            // Check both the flag AND that the delivery type is Solex-eligible
            let isSolexDocEditor = props.deliveryMethods?.some(d =>
                d.CustomAttributes.SolexDocEditorAttr === true &&
                (d.Id?.toLowerCase().startsWith(DELIVERY_PREFIX.ESIGN) ||
                 d.Id?.toLowerCase().startsWith(DELIVERY_PREFIX.ECLOSE))
            ) &&
                currentState.configPlugInData.data?.Configuration?.EnableSolexDocEditor &&
                isSolexEditorFlagEnabled ? true : false;

            const eCloseTypes = props.deliveryMethods?.filter(d => d.Id.toLowerCase().startsWith(DELIVERY_PREFIX.ECLOSE));
            if (eCloseTypes.length > 0) {
                if (eCloseTypes.some(d => d.Id.toLowerCase().includes(DELIVERY_PREFIX.RON)))
                    ronIndicator = true;
                if (eCloseTypes.some(d => d.Id.toLowerCase().includes(DELIVERY_PREFIX.ENOTE)))
                    eNoteIndicator = true;
                if (eCloseTypes.some(d => d.Id.toLowerCase().includes(DELIVERY_PREFIX.IPEN))) {
                    eNotaryIndicator = true;
                    const penVendor = props.deliveryMethods.find(d => d.CustomAttributes?.IPENVendor);
                    iPenVendor = penVendor?.CustomAttributes?.IPENVendor ?? "";
                    eNotaryType = iPenVendor;
                }
            }

            const esignTypes = props.deliveryMethods?.filter(d => d.Id.toLowerCase().startsWith(DELIVERY_PREFIX.ESIGN) || d.Id.toLowerCase().startsWith(DELIVERY_PREFIX.EDISCLOSE));
            if (esignTypes.length > 0) {
                verificationVendorType = props.deliveryMethods?.find(d => d.CustomAttributes.VerificationVendorType)?.CustomAttributes?.VerificationVendorType ?? "";
                formFreeLenderId = props.deliveryMethods?.find(d => d.CustomAttributes.FormFreeLenderID)?.CustomAttributes?.FormFreeLenderID ?? "";
            }

            if (props.buttonText === BUTTON_LABELS.PREVIEW) {
                epcTransactionType = EPC_REQUEST_TYPES.PROCESSING;
            } else if (isClosingPackage()) {
                epcTransactionType = EPC_REQUEST_TYPES.CLOSING;
            }
            
            let request: ITransactionRequest = {
                type: epcTransactionType,
                options: {
                    RequestType: "DocGen",
                    CustomAttributes: {
                        CxIframeVendor: iFrameVendor ?? "",
                        CxIPenVendor: iPenVendor ?? "",
                        CxDeliveryType: props.deliveryMethods?.map(e => e.Id)?.join(','),
                        CxFormFreeLenderID: formFreeLenderId ?? "",
                        CxVerificationVendorType: verificationVendorType ?? ""
                    },
                    DocGenRequest: {
                        LoanId: currentState.appSlice.loanId ?? "",
                        ConfigurationId: currentState.appSlice.configId ?? "",
                        RequestOptions: {
                            RequestType: requestType,
                            PackageType: props.packageId,
                            AlternatePackageType: null,
                            //BundlingType:"Individual",
                            BundlingType: "Merged",
                            IncludeDocumentData: currentState.configPlugInData?.data?.Configuration?.DefaultValues?.IncludeDocumentData ?? false,
                            EnablePreview: enablePreview,
                            Username: currentState.appSlice.userFullName ?? "",
                            PushbackOptions: {
                                PushbackEndpointUrl: process.env.REACT_APP_DT_PUSHBACK_API_URI as string
                            },
                            ImpersonationOption: {
                                ClientCode: currentState.appSlice.clientCode ?? "",
                                EnableImpersonation: currentState.appSlice.clientCode ? true : false
                            },

                        },
                        DeliveryOptions: {
                            Fulfillment: {
                                SendToFulfillment: false,
                                ShippingMethod: ""
                            },
                        },
                    },
                    BorrowerPairTransactionIds: borrowerTransactionIds
                },
                resources: currentState.appSlice.attachments
            }

            if (props.buttonText === BUTTON_LABELS.SEND) {
                request.scope = {
                    type: SCOPE_TYPE.LOAN,
                }
            }
            //alt/parent packageId
            if (props.altPackageId && props.altPackageId.trim() !== "") {
                const parsedAltPackageId = Number(props.packageId);
                if (!isNaN(parsedAltPackageId)) {
                    request.options.DocGenRequest!.RequestOptions.AlternatePackageType = parsedAltPackageId;
                    request.options.DocGenRequest!.RequestOptions.PackageType = props.parentTypeId;
                }
            }

            //For anything but LightsOn properties
            if (props.requestType !== REQUEST_TYPES.LIGHTS_ON) {
                request.options.DocGenRequest!.DeliveryOptions!.SolexEmail = null;
                request.options.DocGenRequest!.DeliveryOptions!.ClosingCollaboration = null;
                if (deliveryEmails != null && deliveryEmails.length > 0) {
                    request.options.DocGenRequest!.DeliveryOptions!.SolexEmail = {
                        Recipients: deliveryEmails
                    }
                }
                if (collabEmails != null && collabEmails.length > 0) {
                    request.options.DocGenRequest!.DeliveryOptions!.ClosingCollaboration = {
                        Recipients: collabEmails
                    }
                }

                request.options.DocGenRequest!.DeliveryOptions!.SolexEnotaryType = eNotaryType;
                request.options.DocGenRequest!.DeliveryOptions!.SolexVerificationVendorType = verificationVendorType;
                request.options.DocGenRequest!.DeliveryOptions!.SolexFormFreeLenderID = formFreeLenderId;
                request.options.DocGenRequest!.DeliveryOptions!.SolexEdisclose = eDiscloseIndicator;
                request.options.DocGenRequest!.DeliveryOptions!.SolexEsign = eSignIndicator;
                request.options.DocGenRequest!.DeliveryOptions!.SolexEclose = eCloseIndicator;
                request.options.DocGenRequest!.DeliveryOptions!.SolexEnote = eNoteIndicator;
                request.options.DocGenRequest!.DeliveryOptions!.SolexEnotary = eNotaryIndicator;
                request.options.DocGenRequest!.DeliveryOptions!.SolexRON = ronIndicator;
                request.options.DocGenRequest!.DeliveryOptions!.EnableSolexEditorLink = isSolexDocEditor;

                //if delivery type starts with 'mail'
                const mailDelivery = props.deliveryMethods.find(x => x.Id.toLowerCase().startsWith('mail'));
                if (mailDelivery) {
                    request.options.DocGenRequest!.DeliveryOptions!.Fulfillment.SendToFulfillment = true;
                    request.options.DocGenRequest!.DeliveryOptions!.Fulfillment.ShippingMethod = mailDelivery.Id.replace('Mail-', '');
                }

                if (props.selectedDocuments.length > 0) {
                    request.options.DocGenRequest!.RequestOptions.Documents = props.selectedDocuments.map(
                        (doc) => ({
                            DocumentIndex: doc.documentIndex ?? null,
                            PrimaryInstanceId: doc.primaryInstanceId ?? null,
                            SecondaryInstanceId: doc.secondaryInstanceId ?? null,
                            Copies: doc.copies ?? null
                        })
                    );
                }

                if (request.resources && request.resources.length > 0) {
                    request.options.DocGenRequest!.RequestOptions!.Attachments = request.resources.map(
                        (att) => ({
                            Name: att.name,
                            Type: att.name.split(".").pop() || null
                        })
                    );
                }
            }
            return request;
        }

        const buildBorrowerTransactionObject = (borrowerPairId: string) => {
            if (!validateLoanAndConfig()) {
                return;
            }
            let request: IBorrowerTransactionRequest = {
                type: EPC_REQUEST_TYPES.DISCLOSURES,                
                scope: {
                    type: SCOPE_TYPE.APPLICATION,
                    id: borrowerPairId
                }
            }
            return request;
        }   
        /// Verify if the conformX response results propeties has any solex error and return a list which contains the error information.
        const prepareSolexErrorList = (docGenResult: IDocGenConformXResponse) => {
            const solexErrors: Array<IErrorDetail> = [];

            const resultKeys: Array<keyof IDocGenConformXResponse> = [
                'esignResult',
                'ecloseResult',
                'emailResult',
                'ediscloseResult',
                'collaborationResult',
            ];

            resultKeys.forEach((key) => {
                const result = docGenResult[key];

                // Check if there are errors and push the first error message to the solexErrors array
                if (result?.errors?.length) {
                    solexErrors.push({ message: result.errors[0] });
                }
            });

            if (solexErrors && solexErrors?.length > 0) {
                setSubmitSolexErrors(solexErrors);
            }
        };

        const handleTransactionSubmission = async (request: ITransactionRequest | null) => {
            if (!request) {
                console.error("Failed to build transaction object");
                props.alertHandler("Failed to build transaction object", "error");
                return;
            }

            const transactionId = await elliHost.createTransaction(request)
                .catch(error => {
                    console.log(error);
                });

            if (transactionId && transactionId.id) {
                dispatch(addRequestId({
                    RequestId: transactionId.id,
                    Date: new Date()
                }));

                // Save order details 
                try {
                    const loanNumber = appState.loanNumber ||'';
                    
                    // Only make API call if loan number is not empty
                    if (loanNumber && loanNumber.trim() !== '') {
                        const orderDetailRequest: ISaveOrderDetailRequest = {
                            configurationId: appState.configId || '',
                            orderDetail: {
                                workstationCreatedEntry: window.location.hostname || '',
                                loanNumber: loanNumber,
                                loanId: appState.loanId || '',
                                borrowerName: appState.borrowerName || '',
                                packageType: props.packageId,
                                deliveryTypes: props.deliveryMethods
                                    .map(dm => dm.Id || dm.Caption || '')
                                    .filter(id => id !== ''),
                                encompassUser: appState.userFullName || '',
                                orderId: transactionId.id
                            }
                        };
                        
                        await pluginService.saveOrderDetails(appState.accessToken, orderDetailRequest);
                    }
                } catch (error) {
                    console.error('Failed to save order details:', error);
                }

                const status = await pollSubmitStatus(transactionId.id);
                if (status?.result?.error) {
                    setSubmitApiError({ exception: { message: status.result.error } });
                    setErrorOpen(true);
                }
                else if (status?.result?.conformXResponse?.errors || status?.result?.conformXResponse?.hasErrors) {
                    prepareSolexErrorList(status.result.conformXResponse);
                    setSubmitErrors(status.result.conformXResponse.errors);
                    setErrorOpen(true);
                }
                else if (status?.result) {
                    // Extract both results to determine which is active (EN-3255, EN-3370)
                    const esignResult = status.result?.conformXResponse?.esignResult;
                    const ecloseResult = status.result?.conformXResponse?.ecloseResult;

                    // Determine delivery method indicators from props (source of truth)
                    const eSignIndicator = props.deliveryMethods?.some(d => d.Id?.toLowerCase().startsWith(DELIVERY_PREFIX.ESIGN)) ?? false;
                    const eCloseIndicator = props.deliveryMethods?.some(d => d.Id?.toLowerCase().startsWith(DELIVERY_PREFIX.ECLOSE)) ?? false;

                    // Use delivery method indicators to determine active result
                    // This is more reliable than link presence, as link can be missing due to errors
                    // while still corresponding to the selected delivery method (EN-3255)
                    const solexLink = eSignIndicator ? esignResult?.solexEditorLink :
                                      eCloseIndicator ? ecloseResult?.solexEditorLink :
                                      null;

                    // Get warnings from the same delivery method result
                    // If neither indicator is set, prefer first non-empty warnings array (with safe navigation)
                    const warnings = eSignIndicator ? esignResult?.warnings :
                                     eCloseIndicator ? ecloseResult?.warnings :
                                     (esignResult?.warnings?.length ? esignResult.warnings : ecloseResult?.warnings);

                    if (solexLink) {
                        // Store link in Redux for persistent access (EN-3255)
                        dispatch(setOrderSolexEditorLink(solexLink));

                        // Auto-open removed per EN-3370 - users will click manual button in success dialog
                        // Note: eSign and eClose are mutually exclusive, so only one link will ever be present
                    }

                    // Store warnings for display in success dialog (only if no link and warnings exist)
                    if (!solexLink && warnings && warnings.length > 0) {
                        setSolexWarnings(warnings);
                    }

                    setConformXURL(status.result?.conformXResponse?.documentRequestImportUrl || '');
                    setSuccessOpen(true);
                }
                else {
                    setOrderFailOpen(true);
                    console.log('Error fetching order status');
                }
            }
        };

        const executeOrderSubmission = async () => {
            //send to EPC via the JS library
            //JS lib sends to API via webhook
            dispatch(setLoadingState({ loading: true, loadingMessage: 'Building request' }));
            if (isClosingPackage()) {
                const request = buildTransactionObject() || null;
                await handleTransactionSubmission(request);
            } else if(props.buttonText === BUTTON_LABELS.SEND) {
                const borrowerPairIds = appState?.borrowerPairIds ?? [];
                if (borrowerPairIds.length > 0) {
                    const borrowerTransactionResults = await Promise.all(
                        borrowerPairIds.map(async (borrowerPairId) => {
                            if (!borrowerPairId) return null;
                            const request = buildBorrowerTransactionObject(borrowerPairId);

                            for (let attempt = 0; attempt <= BORROWER_TX_MAX_RETRIES; attempt++) {
                                try {
                                    const transactionId = await elliHost.createTransaction(request);
                                    if (transactionId?.id) {
                                        return { TransactionId: transactionId.id, BorrowerId: borrowerPairId } as IBorrowerPairTransactionId;
                                    }
                                } catch (error) {
                                    console.error(
                                        `Borrower pair transaction creation failed (attempt ${attempt + 1}/${BORROWER_TX_MAX_RETRIES + 1}) for borrower ${borrowerPairId}:`,
                                        error
                                    );
                                    if (attempt < BORROWER_TX_MAX_RETRIES) {
                                        await new Promise(resolve => setTimeout(resolve, BORROWER_TX_RETRY_DELAY_MS));
                                    }
                                }
                            }
                            return null;
                        })
                    );

                    const failedCount = borrowerTransactionResults.filter(r => r === null).length;
                    if (failedCount > 0) {
                        dispatch(setLoadingState({ loading: false, loadingMessage: '' }));
                        props.alertHandler(
                            `Failed to create transaction for ${failedCount} borrower pair(s). Please try again or contact your administrator.`,
                            'error'
                        );
                        return;
                    }

                    const borrowerTransactionIds = borrowerTransactionResults as IBorrowerPairTransactionId[];
                    const parentTransactionRequest = buildTransactionObject(borrowerTransactionIds) || null;
                    await handleTransactionSubmission(parentTransactionRequest);
                }
            } else {
                const request = buildTransactionObject() || null;
                await handleTransactionSubmission(request);
            }
            dispatch(setLoadingState({ loading: false, loadingMessage: '' }));
            dispatch(setOrderCount(currentOrderCount + 1));
            
            // Reset email states so that next order will ask for emails again
            setDeliveryEmails([]);
            setCollabEmails([]);
            setEmails('');
        }

        const submitOrder = async () => {
            // Clear previous Solex link when starting new order (EN-3255)
            dispatch(setOrderSolexEditorLink(null));
            setSolexWarnings(null); // Also clear previous warnings

            const failedValidations = await handleSendValidations();

            if (failedValidations && failedValidations.length > 0) {
                setExpValidationDialogOpen(true);
                setExpValidationDialogBody(failedValidations.join("\n"));
                return;
            }

            //ask for emails 
            setEmailInputError(false);
            if (props.deliveryMethods.some(x => x.Id.toLowerCase() === 'email') && deliveryEmails.length < 1) {
                setEmailInputType('email')
                setEmailModalText("You have selected an Email delivery method type. This delivery requires emails. Please enter below, separating emails by a comma (,).");
                setOpenEmailModal(true);
                return;
            }
            if (props.deliveryMethods.some(x => x.Id.toLowerCase() === 'collaboration') && collabEmails.length < 1) {
                setEmailInputType('collab')
                setEmailModalText("You have selected a Collaboration delivery method type. This delivery requires emails. Please enter below, separating emails by a comma (,).");
                setOpenEmailModal(true);
                return;
            }

            await executeOrderSubmission();
        }

        const pollSubmitStatus = async (transactionId: string) => {
            dispatch(setLoadingState({ loading: true, loadingMessage: 'Generating Documents' }));
            const waitInterval = 5000;
            const maxPoll = 60;
            let pollCount = 0;
            let consecutiveErrors = 0;
            const maxConsecutiveErrors = 3;
            const apiService = new DocGenApiService();
            
            while (pollCount < maxPoll) {
                try {
                    const response: IDocGenStatusModel = await apiService.getDocGenStatus(transactionId, props.token);
                    
                    // Reset error counter on successful response
                    consecutiveErrors = 0;
                    
                    if (response?.result?.requestStatus === API_REQUEST_STATUS.COMPLETE || 
                        response?.result?.requestStatus === API_REQUEST_STATUS.ERROR) {
                        return response;
                    }
                } catch (error: any) {
                    consecutiveErrors++;
                    console.error(`API Error (attempt ${consecutiveErrors}/${maxConsecutiveErrors}):`, error);
                    
                    // If we've had too many consecutive errors, stop polling
                    if (consecutiveErrors >= maxConsecutiveErrors) {
                        console.error('Too many consecutive API errors. Stopping polling.');
                        dispatch(setLoadingState({ loading: false, loadingMessage: '' }));
                        
                        // Return a mock error response for consistent handling
                        return {
                            result: {
                                requestStatus: API_REQUEST_STATUS.ERROR,
                                error: `API communication failed after ${maxConsecutiveErrors} attempts. Please try again later.`,
                                conformXResponse: null
                            }
                        } as IDocGenStatusModel;
                    }
                    
                    // For transient errors, wait a bit longer before retrying
                    if (consecutiveErrors > 1) {
                        const errorBackoffTime = waitInterval * consecutiveErrors;
                        await new Promise(resolve => setTimeout(resolve, errorBackoffTime));
                    }
                }
                
                pollCount++;
                
                // Only wait if we're continuing to poll
                if (pollCount < maxPoll) {
                    await new Promise(resolve => setTimeout(resolve, waitInterval));
                }
            }
            
            // Timeout scenario - no successful response after maxPoll attempts
            dispatch(setLoadingState({ loading: false, loadingMessage: '' }));
            return {
                result: {
                    requestStatus: API_REQUEST_STATUS.ERROR,
                    error: `Document generation timed out after ${maxPoll * waitInterval / 1000} seconds. Please check the status manually or try again.`,
                    conformXResponse: null
                }
            } as IDocGenStatusModel;
        }

        const onEmailClose = () => {
            const emailArr = emails.split(',');
            if (!checkEmailsValid(emailArr)) {
                setEmailInputError(true);
                return;
            }
            if (emailInputType === 'email')
                setDeliveryEmails(emailArr);
            if (emailInputType === 'collab')
                setCollabEmails(emailArr);
            setOpenEmailModal(false);
            
            // Trigger the submission after email state updates
            setTriggerSubmission(true);
        }

        const onEmailCancel = () => {
            setOpenEmailModal(false);
            setEmails('');
            setEmailInputError(false);
        }

        // Handle email state updates and trigger submission when emails are set
        useEffect(() => {
            if (triggerSubmission && (deliveryEmails.length > 0 || collabEmails.length > 0)) {
                setTriggerSubmission(false);
                executeOrderSubmission();
            }
        }, [deliveryEmails, collabEmails, triggerSubmission]);

        const handleErrorDialogClose = () => {
            setSubmitErrors(null);
            setSubmitApiError(null);
            setSubmitSolexErrors(null);
            setErrorOpen(false);
        }

        const handleSuccessDialogClose = async () => {
            setSuccessOpen(false);
            setSolexWarnings(null); // Reset warnings
            const previewDocs = new PreviewDocsService();
            if (props.requestType === REQUEST_TYPES.LIGHTS_OUT) {
                if (props.alwaysPreviewDocuments || isPrintInPersonSelected())
                    await previewDocs.previewDocument(
                        null,
                        appState.accessToken ?? "",
                        requests
                    );
                else if (props.buttonText === BUTTON_LABELS.PREVIEW)
                    await previewDocs.previewDocument(
                        null,
                        appState.accessToken ?? "",
                        requests
                    );
            } else if (props.requestType === REQUEST_TYPES.LIGHTS_ON) {
                if (conformXURL) {
                    elliHost.openResource({ target: conformXURL });
                    setConformXURL("");
                } else {
                    console.error("conformXURL is empty or undefined");
                }
            }
        }
        const handleErrorClose = () => {
            setOrderFailOpen(false);
        }
        const checkEmailsValid = (emails: Array<string>) => {
            for (let i = 0; i < emails.length; i++) {
                let email = emails[i].trim();
                if (email.search(EMAIL_REGEX) === -1)
                    return false;
                }
            return true;
        }

        const handleSendValidations = async (): Promise<string[]> => {
            const sendValidations = props.sendValidations;
            const enabledValidations = sendValidations?.filter(x => x.Enabled);
            const failedExpressionsArray: string[] = [];


            if (enabledValidations && enabledValidations?.length > 0) {

                const results: IExpression[] = await GetExpressionEvaluationResults(enabledValidations, enhancedFieldIdValuePairs, props.packageId);
                const expressionArray = results.filter(exp => exp.Enabled).map((exp: IExpression) => exp.ExpressionString);
                const evalResults: IExpressionEvaluation[] = (await expService.validateExpressions(accessToken, expressionArray)).result;

                const filterFailedExpressions: IExpressionEvaluation[] = evalResults?.filter(exp => !exp.evaluationResult);

                if (filterFailedExpressions && filterFailedExpressions.length) {
                    filterFailedExpressions.forEach(res => { //filter the expression names of all failed expression strings
                        const matchedValidations = enabledValidations.filter(exp => res.expression === addRegexExp(exp.ExpressionString, enhancedFieldIdValuePairs, props.packageId));
                        //Remove duplicate failed expression names
                        matchedValidations?.forEach((exp: IExpression) => {
                            if (!failedExpressionsArray.includes(exp.ExpressionName)) {
                                failedExpressionsArray.push(exp.ExpressionName);
                            }
                        });
                        return failedExpressionsArray;
                    });
                }
                else {
                    return failedExpressionsArray;
                }
            }
            return failedExpressionsArray;
        }

        return (
            <>
                <StyledHeaderButton
                    size="small"
                    color="success"
                    disabled={props.disabled}
                    startIcon={props.startIcon}
                    onClick={() => submitOrder()}>{props.buttonText}</StyledHeaderButton>

                <EmailInput title="Email Input"
                    text={emailModalText} open={openEmailModal}
                    error={emailInputError}
                    onClose={onEmailClose} 
                    onCancel={onEmailCancel}
                    submitButtonText="OK"
                    emails={emails} setEmails={setEmails} />

                <ErrorModal
                    title="Errors submitting"
                    errors={submitErrors}
                    apiError={submitAPiError}
                    solexErrors={submitSolexErrors}
                    modalOpen={errorOpen}
                    handleModalClose={handleErrorDialogClose}
                />
                <GenericDialog open={successOpen} title="Successful submission" onClose={handleSuccessDialogClose} cancelButtonText="Ok">
                    <Typography>Your order has been submitted</Typography>

                    {/* Show Solex Document Editor button or warnings (EN-3255, EN-3370) */}
                    <SolexDisplay
                        solexEditorLink={solexEditorLinkFromRedux}
                        warnings={solexWarnings}
                        deliveryMethods={props.deliveryMethods}
                        solexEditor={solexEditor}
                    />
                </GenericDialog>
                <GenericDialog open={orderFailOpen} title={"Error"} onClose={handleErrorClose} >
                    <Typography>There was an error fetching your order</Typography>
                </GenericDialog>
                <DtStatusDialog
                    status="error"
                    title="Unable to Preview/Submit"
                    open={expValidationDialogOpen}
                    onClose={() => setExpValidationDialogOpen(false)} 
                    message={<Typography style={{ whiteSpace: 'pre-wrap' }}>{expValidationDialogBody}</Typography>}
                />
            </>
        )
    }

export default OrderButton
